import { moneefloApi, tokens } from "./client";

export {
  moneefloApi,
  tokens,
  apiErrorMessage,
  MONEEFLO_API_URL,
} from "./client";

export type Me = {
  id: string;
  fullName: string;
  email: string;
  organization: { id: string; name: string } | null;
  permissions: string[];
};

export type CustomerListItem = {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  type: string;
  lifecycleStage: string;
  owner: string | null;
  city: string | null;
};

export type OrderListItem = {
  id: string;
  orderNo: string;
  status: string;
  paymentStatus: string;
  product: string | null;
  customer: { id: string; name: string };
  currency: string | null;
  totalPayableMinor: string | null;
  placedAt: string | null;
};

export type Paged<T> = {
  items: T[];
  page: number;
  perPage: number;
  total: number;
};

export async function login(email: string, password: string): Promise<Me> {
  const { data } = await moneefloApi.post("/auth/login", { email, password });
  tokens.set(data.accessToken, data.refreshToken);
  return me();
}

export async function logout(): Promise<void> {
  const refreshToken = tokens.refresh;
  if (refreshToken) {
    await moneefloApi
      .post("/auth/logout", { refreshToken })
      .catch(() => undefined);
  }
  tokens.clear();
}

export async function forgotPassword(email: string): Promise<string> {
  const { data } = await moneefloApi.post("/auth/forgot-password", { email });
  return data.message;
}

/** Minimum the backend will accept; the screen checks before asking. */
export const PASSWORD_MIN_LENGTH = 12;

export async function resetPassword(
  token: string,
  password: string,
): Promise<string> {
  const { data } = await moneefloApi.post("/auth/reset-password", {
    token,
    password,
  });
  return data.message;
}

export async function me(): Promise<Me> {
  const { data } = await moneefloApi.get("/auth/me");
  return data;
}

export async function listCustomers(params: {
  search?: string;
  stage?: string;
  page?: number;
  perPage?: number;
}): Promise<Paged<CustomerListItem>> {
  const { data } = await moneefloApi.get("/customers", { params });
  return data;
}

export async function listOrders(params: {
  search?: string;
  status?: string;
  category?: string;
  page?: number;
  perPage?: number;
}): Promise<Paged<OrderListItem>> {
  const { data } = await moneefloApi.get("/orders", { params });
  return data;
}

export async function listProducts(): Promise<
  { id: string; code: string; name: string; category: string }[]
> {
  const { data } = await moneefloApi.get("/catalog/products");
  return data;
}

export type Purpose = {
  id: string;
  name: string;
  tcsType: string | null;
  isLoan: boolean;
  product: { id: string; code: string; name: string };
};

export async function listPurposes(productId: string): Promise<Purpose[]> {
  const { data } = await moneefloApi.get("/catalog/purposes", {
    params: { productId },
  });
  return data;
}

export async function listCurrencies(): Promise<
  { code: string; name: string }[]
> {
  const { data } = await moneefloApi.get("/catalog/currencies");
  return data;
}

/** `id` is the user's public id, which is what the API expects back. */
export type StaffOption = { id: string; fullName: string; email: string };

export async function listStaff(search?: string): Promise<StaffOption[]> {
  const { data } = await moneefloApi.get("/users", {
    params: { search, perPage: 100 },
  });
  return data.items ?? data;
}

export type QuoteLine = {
  type: string;
  label: string;
  amountMinor: string;
  taxMinor: string;
};

/** What the server says a remittance costs. Cost fields are staff-only. */
export type Quote = {
  currency: string;
  fxAmountMinor: string;
  customerRate: string;
  baseAmountMinor: string;
  lines: QuoteLine[];
  chargesMinor: string;
  taxMinor: string;
  totalPayableMinor: string;
  tcsPurposeType: string;
  servicePartnerId?: string;
  settlementRate?: string;
  partnerCostMinor?: string;
  marginMinor?: string;
  commissions: {
    organizationId: string;
    partnerLevel: number;
    amountMinor: string;
  }[];
  unpricedPartners?: string[];
};

export type QuoteRequest = {
  customerId?: string;
  productId: string;
  purposeId?: string;
  currency: string;
  fxAmount: number;
  servicePartnerId?: string;
  addOnRate?: number;
  ttCharge?: number;
  foreignBankCharge?: number;
  serviceFee?: number;
  discount?: number;
};

export async function quoteOrder(body: QuoteRequest): Promise<Quote> {
  const { data } = await moneefloApi.post("/orders/quote", body);
  return data;
}

export async function createOrder(
  body: QuoteRequest & {
    customerId: string;
    beneficiaryId?: string;
    initialStatus?: "draft" | "quoted";
  },
): Promise<{ id: string; orderNo: string }> {
  const { data } = await moneefloApi.post("/orders", body);
  return data;
}

export type HomePeriod = {
  grossMinor: string;
  netMinor: string;
  volumeMinor: string;
  orders: number;
  marginRate: string;

  /** The part of net sitting on orders the customer has actually paid. */
  paidNetMinor: string;
  /** The same figures per product, biggest first. */
  byProduct: HomeProductFigures[];
  /** And per category, which is what the dashboard tabs between. */
  byCategory: HomeCategoryFigures[];
};

export type HomeProductFigures = {
  product: string;
  category: string | null;
  orders: number;
  volumeMinor: string;
  grossMinor: string;
  netMinor: string;
};

export type HomeCategoryFigures = {
  category: string;
  orders: number;
  volumeMinor: string;
  grossMinor: string;
  netMinor: string;
  marginRate: string;
};

export type HomeStats = {
  today: HomePeriod;
  week: HomePeriod;
  month: HomePeriod;
  deals: { unutilized: number };
};

export async function homeStats(): Promise<HomeStats> {
  const { data } = await moneefloApi.get("/dashboard/home");
  return data;
}

export type CreatedCustomer = { id: string; fullName: string };

export async function createCustomer(body: {
  fullName: string;
  phone?: string;
  email?: string;
  city?: string;
  state?: string;
  source?: string;
  type?: string;
}): Promise<CreatedCustomer> {
  const { data } = await moneefloApi.post("/customers", body);
  return data;
}

export async function createOpportunity(body: {
  customerId: string;
  productId?: string;
  purposeId?: string;
  expectedAmount?: number;
  currency?: string;
  country?: string;
  notes?: string;
  nextFollowUpAt?: string;
  assignedUserId?: string;
}): Promise<{ id: string }> {
  const { data } = await moneefloApi.post("/opportunities", body);
  return data;
}

/** Paise to rupees, for display only. */
export function formatMinor(minor: string | null, currency = "INR"): string {
  if (!minor) return "-";
  const value = Number(minor) / 100;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}
